import validate from 'validate.js';

export interface CustomerFormData {
  fullName: string;
  customerType: string;
  email: string;
  phone: string;
  companyName: string;
  contactPerson: string;
  streetArea: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  creditLimit: string;
  currentBalance: string;
  notes: string;
  package?: string;
}

export const customerValidationConstraints = {
  fullName: {
    presence: { allowEmpty: false, message: 'Full name is required' },
    length: {
      maximum: 255,
      message: 'Full name must be less than 255 characters'
    }
  },
  email: {
    presence: { allowEmpty: false, message: 'Email is required' },
    email: { message: 'Email must be a valid email address' },
    length: {
      maximum: 255,
      message: 'Email must be less than 255 characters'
    }
  },
  phone: {
    presence: { allowEmpty: false, message: 'Phone number is required' },
    length: {
      maximum: 20,
      message: 'Phone number must be less than 20 characters'
    }
  },
  customerType: {
    presence: { allowEmpty: false, message: 'Status is required' },
    inclusion: {
      within: ['active', 'inactive', 'suspended'],
      message: 'Status must be one of: active, inactive, suspended'
    }
  },
  companyName: {
    length: {
      maximum: 255,
      message: 'Company name must be less than 255 characters'
    }
  },
  contactPerson: {
    length: {
      maximum: 255,
      message: 'Contact person name must be less than 255 characters'
    }
  },
  streetArea: {
    length: {
      maximum: 1000,
      message: 'Address must be less than 1000 characters'
    }
  },
  city: {
    length: {
      maximum: 255,
      message: 'City must be less than 255 characters'
    }
  },
  state: {
    length: {
      maximum: 255,
      message: 'State must be less than 255 characters'
    }
  },
  country: {
    length: {
      maximum: 255,
      message: 'Country must be less than 255 characters'
    }
  },
  postalCode: {
    length: {
      maximum: 20,
      message: 'Postal code must be less than 20 characters'
    }
  },
  creditLimit: function(value: string) {
    if (!value || value.trim() === '') return null;
    
    const numValue = parseFloat(value);
    
    if (isNaN(numValue)) {
      return ['Credit limit must be a valid number'];
    }
    
    if (numValue < 0) {
      return ['Credit limit cannot be negative'];
    }
    
    return null;
  },
  currentBalance: function(value: string) {
    if (!value || value.trim() === '') return null;
    
    const numValue = parseFloat(value);
    
    if (isNaN(numValue)) {
      return ['Current balance must be a valid number'];
    }
    
    return null;
  }
};

export const validateCustomerForm = (formData: CustomerFormData) => {
  console.log('=== VALIDATION UTILITY DEBUG ===');
  console.log('Input formData:', formData);
  console.log('Constraints:', customerValidationConstraints);
  
  // Use default format to get field-specific errors
  const errors = validate(formData, customerValidationConstraints);
  
  console.log('Raw validate.js errors:', errors);
  
  if (errors) {
    console.log('Final formatted errors:', errors);
    return errors;
  }
  
  console.log('No validation errors found');
  return null;
};

// Helper to format errors for display
export const getValidationError = (fieldName: string, errors: any) => {
  console.log(`getValidationError called for ${fieldName}:`, errors);
  
  if (errors && errors[fieldName]) {
    // If it's an array, join the messages
    if (Array.isArray(errors[fieldName])) {
      const result = errors[fieldName].join(', ');
      console.log(`Returning array result for ${fieldName}:`, result);
      return result;
    }
    console.log(`Returning single result for ${fieldName}:`, errors[fieldName]);
    return errors[fieldName];
  }
  
  console.log(`No error found for ${fieldName}`);
  return '';
};
